import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  CssBaseline,
  ThemeProvider,
  createTheme
} from '@mui/material';

import GoogleDriveConnectionStep from './components/GoogleDriveConnectionStep';
import UrlInput from './components/UrlInput';
import SiteMap from './components/SiteMap';
import PageSelector from './components/PageSelector';
import UtilityBar from './components/UtilityBar';
import ProgressMonitor from './components/ProgressMonitor';
import DestinationFolderDisplay from './components/DestinationFolderDisplay';
import ScrapingResults from './components/ScrapingResults';
import { mapSite, startScraping } from './api';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#fafafa',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
  },
});

// const steps = [
//   'Connect Google Drive',
//   'Enter Website URL',
//   'Map Site Structure',
//   'Select Pages to Scrape',
//   'Start Scraping',
//   'View Results'
// ];

function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [expandedSteps, setExpandedSteps] = useState(new Set([0])); // Track which steps are expanded
  const [url, setUrl] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [mapJobId, setMapJobId] = useState(null);
  const [siteMap, setSiteMap] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [scrapingJobId, setScrapingJobId] = useState(null);
  const [scrapingResult, setScrapingResult] = useState(null);
  const [error, setError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [driveConnectionDecision, setDriveConnectionDecision] = useState(null); // null, 'connect', 'skip'
  const [workflowStarted, setWorkflowStarted] = useState(false); // Track if URL entry has started

  // Handle authentication redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authStatus = urlParams.get('auth');
    const authMessage = urlParams.get('message');
    
    if (authStatus === 'success') {
      setAuthMessage('Google Drive authentication successful!');
      setDriveConnectionDecision('connect');
      setIsDriveConnected(true);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      // Move to next step (URL entry)
      setCompletedSteps(prev => new Set(prev).add(0));
      setActiveStep(1);
      setExpandedSteps(prev => new Set(prev).add(1));
    } else if (authStatus === 'error') {
      setAuthMessage(`Authentication failed: ${authMessage || 'Unknown error'}`);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Handle initial Drive connection status
  useEffect(() => {
    if (isDriveConnected && driveConnectionDecision === null && !completedSteps.has(0)) {
      // If Drive is connected on app load and we haven't made a decision yet, mark step 0 as completed
      setCompletedSteps(prev => new Set(prev).add(0));
      setDriveConnectionDecision('connect');
      // If we're still on step 0, move to step 1
      if (activeStep === 0) {
        setActiveStep(1);
        setExpandedSteps(prev => new Set(prev).add(1));
      }
    }
  }, [isDriveConnected, driveConnectionDecision, completedSteps, activeStep]);

  const handleUrlValidated = async (result) => {
    setValidationResult(result);
    setWorkflowStarted(true); // Mark workflow as started
    // Use the originalUrl that was validated, or fallback to result.url or current url
    const validatedUrl = result.originalUrl || result.url || url;
    setUrl(validatedUrl);
    if (result.valid) {
      // Mark step 1 (URL entry) as completed and move to step 2 (site mapping)
      setCompletedSteps(prev => new Set(prev).add(1));
      setActiveStep(2);
      setExpandedSteps(prev => new Set(prev).add(2));
      
      // Automatically start site mapping
      try {
        setError('');
        const mapResult = await mapSite(
          validatedUrl, 
          result.maxDepth || 3,  // Recommended depth
          result.maxPages || 1000,  // Recommended max pages
          result.urlFilters || null  // URL filters (include/exclude patterns)
        );
        setMapJobId(mapResult.job_id);
      } catch (err) {
        setError(err.message);
      }
    }
  };


  const handleSiteMapReady = (map) => {
    setSiteMap(map);
    // Mark step 2 (site mapping) as completed and move to step 3 (page selection)
    setCompletedSteps(prev => new Set(prev).add(2));
    setActiveStep(3);
    setExpandedSteps(prev => new Set(prev).add(3));
  };

  const handleStartScraping = async (urls) => {
    try {
      setError('');
      setSelectedPages(urls);
      
      // Mark step 3 (page selection) as completed when pages are selected
      setCompletedSteps(prev => new Set(prev).add(3));
      
      const folderId = selectedFolder?.id || null;
      const result = await startScraping(urls, folderId);
      setScrapingJobId(result.job_id);
      setActiveStep(4);
      setExpandedSteps(prev => new Set(prev).add(4));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleScrapingComplete = (result) => {
    console.log('Scraping completed:', result);
    setScrapingResult(result);
    // Mark scraping step as completed and move to results step
    setCompletedSteps(prev => new Set(prev).add(4));
    setActiveStep(5); // Move to View Results step
    setExpandedSteps(prev => new Set(prev).add(5));
  };

  const handleScrapingCancel = () => {
    console.log('Scraping cancelled');
    // Reset to page selection step
    setActiveStep(3);
    setScrapingJobId(null);
  };

  const handleFolderSelected = (folder) => {
    setSelectedFolder(folder);
  };

  const handleDriveConnectionChange = (isConnected) => {
    setIsDriveConnected(isConnected);
    if (!isConnected) {
      setSelectedFolder(null);
    } else {
      // If Drive becomes connected and we haven't made a decision yet, mark step 0 as completed
      if (driveConnectionDecision === null && !completedSteps.has(0)) {
        setCompletedSteps(prev => new Set(prev).add(0));
        setDriveConnectionDecision('connect');
        // If we're still on step 0, move to step 1
        if (activeStep === 0) {
          setActiveStep(1);
          setExpandedSteps(prev => new Set(prev).add(1));
        }
      }
    }
  };

  const handleDriveDecision = (decision) => {
    setDriveConnectionDecision(decision);
    if (decision === 'skip') {
      // Mark step 0 as completed and move to step 1 (URL entry)
      setCompletedSteps(prev => new Set(prev).add(0));
      setActiveStep(1);
      setExpandedSteps(prev => new Set(prev).add(1));
    }
    // If decision is 'connect', the authentication redirect will handle moving to the next step
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompletedSteps(new Set());
    setExpandedSteps(new Set([0]));
    setUrl('');
    setValidationResult(null);
    setMapJobId(null);
    setSiteMap(null);
    setSelectedPages([]);
    setSelectedFolder(null);
    setScrapingJobId(null);
    setScrapingResult(null);
    setError('');
    setAuthMessage('');
    setDriveConnectionDecision(null);
    setWorkflowStarted(false);
    setIsDriveConnected(false);
  };

  // const handleDownloadAll = () => {
  //   if (scrapingJobId) {
  //     const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  //     const downloadUrl = `${backendUrl}/api/download/job/${scrapingJobId}`;
  //     window.location.href = downloadUrl;
  //   }
  // };

  const handleBack = () => {
    const newStep = activeStep - 1;
    setActiveStep(newStep);
    // Ensure the step we're going back to is expanded
    setExpandedSteps(prev => new Set(prev).add(newStep));
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    setExpandedSteps(prev => new Set(prev).add(activeStep + 1));
  };

  const handleStepClick = (stepIndex) => {
    console.log(`Step ${stepIndex} clicked. Current active: ${activeStep}, Completed: ${Array.from(completedSteps)}, Expanded: ${Array.from(expandedSteps)}`);
    
    // Allow clicking on completed steps or steps that are accessible (up to current active step)
    if (completedSteps.has(stepIndex) || stepIndex <= activeStep) {
      const newExpanded = new Set(expandedSteps);
      
      // Always allow toggling expansion for any clickable step
      if (newExpanded.has(stepIndex)) {
        // If it's the active step and it's the only expanded step, don't collapse it
        if (stepIndex === activeStep && newExpanded.size === 1) {
          console.log('Cannot collapse the only active step');
          return;
        }
        newExpanded.delete(stepIndex);
        console.log(`Collapsing step ${stepIndex}`);
      } else {
        newExpanded.add(stepIndex);
        console.log(`Expanding step ${stepIndex}`);
      }
      
      // If clicking on a different step that's not active, make it active
      if (stepIndex !== activeStep && (completedSteps.has(stepIndex) || stepIndex < activeStep)) {
        console.log(`Setting step ${stepIndex} as active`);
        setActiveStep(stepIndex);
      }
      
      setExpandedSteps(newExpanded);
    } else {
      console.log(`Step ${stepIndex} is not clickable`);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Utility Bar with Google Drive Status */}
      <UtilityBar 
        url={url}
        onFolderSelected={handleFolderSelected}
        selectedFolder={selectedFolder}
        onConnectionChange={handleDriveConnectionChange}
        workflowStarted={workflowStarted}
        driveConnectionDecision={driveConnectionDecision}
      />

      {/* Destination Folder Display */}
      <DestinationFolderDisplay
        selectedFolder={selectedFolder}
        onSelectFolder={handleFolderSelected}
        isConnected={isDriveConnected}
      />

      <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            mb: 2
          }}>
            Web Content Scraper
          </Typography>
          
          <Typography variant="subtitle1" color="text.secondary" sx={{ 
            fontSize: '1.125rem',
            fontWeight: 400,
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Scrape website content responsibly with robots.txt compliance and Google Drive integration
          </Typography>
        </Box>

        {/* Authentication Status Message */}
        {authMessage && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: authMessage.includes('successful') ? 'success.main' : 'error.main',
                fontWeight: 500,
                backgroundColor: authMessage.includes('successful') ? 'success.light' : 'error.light',
                padding: 2,
                borderRadius: 1,
                border: 1,
                borderColor: authMessage.includes('successful') ? 'success.main' : 'error.main'
              }}
            >
              {authMessage}
            </Typography>
          </Box>
        )}

        {error && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'error.light', color: 'error.contrastText' }}>
            <Typography>Error: {error}</Typography>
          </Paper>
        )}

        {/* Show instruction when there are completed steps */}
        {completedSteps.size > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.contrastText">
              💡 <strong>Tip:</strong> Click on any step label to expand/collapse it and view responses. You can navigate back to previous steps by clicking on them.
            </Typography>
          </Box>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 0: Google Drive Connection */}
          <Step completed={completedSteps.has(0)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 0 clicked');
                handleStepClick(0);
              }}
              sx={{ 
                cursor: completedSteps.has(0) || 0 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(0) || 0 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              Connect Google Drive
            </StepLabel>
            <StepContent expanded={expandedSteps.has(0)}>
              <GoogleDriveConnectionStep 
                onDecision={handleDriveDecision}
                decision={driveConnectionDecision}
                isConnected={isDriveConnected}
              />
            </StepContent>
          </Step>

          {/* Step 1: URL Input */}
          <Step completed={completedSteps.has(1)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 1 clicked');
                handleStepClick(1);
              }}
              sx={{ 
                cursor: completedSteps.has(1) || 1 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(1) || 1 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              Enter Website URL
            </StepLabel>
            <StepContent expanded={expandedSteps.has(1)}>
              <UrlInput 
                onUrlValidated={handleUrlValidated}
                initialUrl={url}
                initialValidationResult={validationResult}
              />
              {/* Show summary when completed and collapsed */}
              {completedSteps.has(1) && !expandedSteps.has(1) && validationResult && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 1, opacity: 0.7 }}>
                  <Typography variant="caption" display="block">
                    ✓ URL validated: {url}
                  </Typography>
                  {validationResult.title && (
                    <Typography variant="caption" display="block">
                      Title: {validationResult.title}
                    </Typography>
                  )}
                  {!validationResult.title && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (No title available)
                    </Typography>
                  )}
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 2: Site Mapping */}
          <Step completed={completedSteps.has(2)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 2 clicked');
                handleStepClick(2);
              }}
              sx={{ 
                cursor: completedSteps.has(2) || 2 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(2) || 2 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              Map Site Structure
            </StepLabel>
            <StepContent expanded={expandedSteps.has(2)}>
              {mapJobId && (
                <SiteMap 
                  mapJobId={mapJobId}
                  onSiteMapReady={handleSiteMapReady}
                />
              )}
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                  disabled={!siteMap}
                >
                  Continue to Page Selection
                </Button>
                <Button
                  onClick={handleBack}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </Box>
              {/* Show summary when completed and collapsed */}
              {completedSteps.has(2) && !expandedSteps.has(2) && siteMap && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 1, opacity: 0.7 }}>
                  <Typography variant="caption" display="block">
                    ✓ Site mapped: {siteMap.total_pages} pages found ({siteMap.accessible_pages} accessible)
                  </Typography>
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 3: Page Selection */}
          <Step completed={completedSteps.has(3)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 3 clicked');
                handleStepClick(3);
              }}
              sx={{ 
                cursor: completedSteps.has(3) || 3 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(3) || 3 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              Select Pages to Scrape
            </StepLabel>
            <StepContent expanded={expandedSteps.has(3)}>
              {siteMap && (
                <PageSelector 
                  siteMap={siteMap}
                  onStartScraping={handleStartScraping}
                  initialSelectedPages={selectedPages}
                />
              )}
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                  disabled={selectedPages.length === 0}
                >
                  {selectedPages.length > 0 ? 
                    `Start Scraping (${selectedPages.length} pages)` : 
                    'Select Pages First'
                  }
                </Button>
                <Button
                  onClick={handleBack}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </Box>
              {/* Show summary when completed and collapsed */}
              {completedSteps.has(3) && !expandedSteps.has(3) && selectedPages.length > 0 && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 1, opacity: 0.7 }}>
                  <Typography variant="caption" display="block">
                    ✓ Selected {selectedPages.length} pages for scraping
                  </Typography>
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 4: Progress Monitoring */}
          <Step completed={completedSteps.has(4)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 4 clicked');
                handleStepClick(4);
              }}
              sx={{ 
                cursor: completedSteps.has(4) || 4 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(4) || 4 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              Scraping in Progress
            </StepLabel>
            <StepContent expanded={expandedSteps.has(4)}>
              {scrapingJobId && (
                <ProgressMonitor 
                  scrapingJobId={scrapingJobId}
                  onComplete={handleScrapingComplete}
                  onCancel={handleScrapingCancel}
                />
              )}
            </StepContent>
          </Step>

          {/* Step 5: View Results */}
          <Step completed={completedSteps.has(5)}>
            <StepLabel 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('StepLabel 5 clicked');
                handleStepClick(5);
              }}
              sx={{ 
                cursor: completedSteps.has(5) || 5 <= activeStep ? 'pointer' : 'default',
                '&:hover': completedSteps.has(5) || 5 <= activeStep ? { backgroundColor: 'action.hover' } : {},
                borderRadius: 1,
                px: 1,
                transition: 'background-color 0.2s'
              }}
            >
              View Results
            </StepLabel>
            <StepContent expanded={expandedSteps.has(5)}>
              {completedSteps.has(4) && scrapingResult && (
                <ScrapingResults 
                  scrapingJobId={scrapingJobId}
                  scrapingResult={scrapingResult}
                  onStartNew={handleReset}
                  selectedFolder={selectedFolder}
                />
              )}
            </StepContent>
          </Step>
        </Stepper>


        {/* Footer */}
        <Box sx={{ mt: 6, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            This scraper respects robots.txt, implements rate limiting, 
            and only scrapes publicly accessible content.
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
